import { Link } from '@televet/kibble-ui/build/components/Link';
import format from 'date-fns/format';
import React from 'react';
import { StripePayoutSelectionFragment } from 'shared/types/graphql';
import { DATE_FORMAT } from '../constants';
import { StatusDescription } from '../records/status.records';
import { DisplayType, IColumnSelection } from '../types';
import { formatMoney } from './formatMoney';
import { dataPlaceholder, getStatusComponent } from './formatting';

interface BaseDisbursementsData {
  rawData: StripePayoutSelectionFragment[];
  displayType: DisplayType;
}

interface InteractiveDisbursementsData extends BaseDisbursementsData {
  displayType: 'Interactive';
  onClickCallback: ({ id, payoutDate }: { id: string; payoutDate: string }) => void;
  columns?: never;
  hasEOD?: never;
}

interface DownloadDisbursementsData extends BaseDisbursementsData {
  displayType: 'Download';
  onClickCallback?: never;
  columns: IColumnSelection[];
  hasEOD: boolean;
}

type BuildDisbursementsDataInput = InteractiveDisbursementsData | DownloadDisbursementsData;

interface BaseDisbursementTableRow {
  createdAt: string;
  endOfDay?: string; // Optional since it depends on a FF
  arrivalDate: string;
}

interface InteractiveDisbursementTableRow extends BaseDisbursementTableRow {
  linkedTransactions: JSX.Element;
  amount: string;
  paymentStatus: JSX.Element | string;
}

interface DownloadDisbursementTableRow extends BaseDisbursementTableRow {
  linkedTransactions?: never;
  amount: number;
  paymentStatus: string;
}

export type DisbursementTableRow = InteractiveDisbursementTableRow | DownloadDisbursementTableRow;

const buildBaseData = ({
  rawData,
  displayType,
  onClickCallback,
  hasEOD,
}: {
  rawData: StripePayoutSelectionFragment;
  displayType: DisplayType;
  onClickCallback?: ({ id, payoutDate }: { id: string; payoutDate: string }) => void;
  hasEOD: boolean;
}): DisbursementTableRow => {
  const emptyPlaceholder = dataPlaceholder[displayType];

  const baseData = {
    createdAt: format(new Date(rawData.initiatedAt), DATE_FORMAT) || emptyPlaceholder,
    endOfDay: rawData?.payoutBatchingPeriod?.endsAt
      ? format(new Date(rawData?.payoutBatchingPeriod?.endsAt), DATE_FORMAT)
      : emptyPlaceholder,
    arrivalDate: format(new Date(rawData.arrivalDate.replace(/Z$/, '')), 'MM/dd/yy') || emptyPlaceholder,
  };

  if (displayType === 'Download') {
    const data: DownloadDisbursementTableRow = {
      ...baseData,
      amount: rawData.amount / 100,
      paymentStatus: StatusDescription[rawData.status] || emptyPlaceholder,
    };

    if (!hasEOD) {
      delete data.endOfDay;
    }

    return data;
  }

  const data: InteractiveDisbursementTableRow = {
    ...baseData,
    amount: formatMoney(rawData.amount),
    paymentStatus: getStatusComponent({ status: rawData.status }) || emptyPlaceholder,
    linkedTransactions: (
      <Link
        size="sm"
        variant="success"
        onClick={(): void => {
          if (onClickCallback) {
            onClickCallback({ id: rawData.id, payoutDate: baseData.arrivalDate });
          }
        }}
      >
        View
      </Link>
    ),
  };

  return data;
};

export const buildDisbursementsData = ({
  rawData,
  displayType,
  columns,
  hasEOD,
  onClickCallback,
}: BuildDisbursementsDataInput): DisbursementTableRow[] => {
  if (!rawData.length) {
    return [];
  }

  const isDownloadData = displayType === 'Download';
  const columnsToRemove = !!columns?.length
    ? (columns.filter((col) => col.omit).map((col) => col.selector) as (keyof DisbursementTableRow)[])
    : [];

  const data: DisbursementTableRow[] = [];
  for (const _data of rawData || []) {
    const row = buildBaseData({
      rawData: _data,
      displayType,
      onClickCallback,
      hasEOD: !!hasEOD,
    });

    if (isDownloadData && !!columnsToRemove.length) {
      columnsToRemove.forEach((column) => delete row[column]);
    }

    data.push(row);
  }

  return data;
};
